/**
 * While an admin views the app as another user, every screen must say so and offer the way back, whatever the target
 * is allowed to see. This strip names both identities, the mode and the time left, and its Return button is the one
 * exit that does not depend on any page the target can reach.
 */
import React, { useEffect, useReducer, useState } from 'react';
import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { getRemainingMinutes } from 'src/lib/view-as/view-as-store';
import type { UseViewAsResult } from './useViewAs';

const COUNTDOWN_REFRESH_MS = 60_000;

function useMinuteTick (): void {
  const [, tick] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    const intervalId = setInterval(tick, COUNTDOWN_REFRESH_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, []);
}

export const ViewAsBanner: React.FC<{ readonly viewAs: UseViewAsResult }> = ({ viewAs }) => {
  const { t } = useTranslation();
  const [isStopping, setIsStopping] = useState(false);
  useMinuteTick();

  const { echo } = viewAs;
  // Read on every tick, not from the prop, so the countdown moves without a parent re-render.
  const remainingMinutes = getRemainingMinutes();
  if (echo === undefined) {
    return null;
  }

  const params = { minutes: String(remainingMinutes), mode: echo.mode, realUser: echo.realUser, target: echo.target };
  const message = remainingMinutes === undefined
    ? t('view_as_banner', params)
    : t('view_as_banner_with_minutes', params);

  return (
    <div className="view-as-banner" data-testid="view-as-banner" style={{ flexShrink: 0 }}>
      <Alert size="small" type="error" width="100%">
        <Row alignmentHorizontal="justify" spacing="400" widths={['fill', 'fit']}>
          <Text color="inverted" data-testid="view-as-banner-message">{message}</Text>
          <Button
            data-testid="view-as-return"
            disabled={isStopping}
            size="small"
            type="secondary"
            onClick={() => {
              setIsStopping(true);
              void viewAs.stop();
            }}
          >
            {t('view_as_return')}
          </Button>
        </Row>
      </Alert>
    </div>
  );
};
